import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// One-time migration: force-default to English on first load after the
// default-language change. Users who explicitly pick Spanish via the language
// toggle will still have their preference respected (cached key remains).
try {
  const MIGRATION_KEY = "canales.lang.default_v2";
  if (typeof window !== "undefined" && !localStorage.getItem(MIGRATION_KEY)) {
    localStorage.setItem("canales.lang", "en");
    localStorage.setItem(MIGRATION_KEY, "1");
  }
} catch (_) { /* ignore storage errors */ }

const resources = {
  es: {
    translation: {
      brand: "Mueblería Canales",
      app_name: "Auditoría Operativa",
      cookies: {
        title: "Este sitio usa cookies",
        text: "Usamos únicamente cookies esenciales para la autenticación segura de tu sesión y para recordar tu preferencia de idioma. No usamos cookies de publicidad ni de rastreo de terceros.",
        accept: "Aceptar",
        essential: "Solo esenciales",
        more: "Política de Privacidad",
        manage: "Preferencias de cookies",
        current_choice: "Selección actual: {{choice}}",
        choice_all: "Todas",
        choice_essential: "Solo esenciales",
        choice_none: "Sin definir",
        change: "Cambiar",
        reset_confirm: "Se restablecerán tus preferencias. La próxima vez que cargues cualquier página verás el banner de nuevo.",
      },
      common: {
        login: "Iniciar sesión",
        logout: "Cerrar sesión",
        email: "Correo electrónico",
        password: "Contraseña",
        name: "Nombre",
        role: "Rol",
        enter: "Entrar",
        cancel: "Cancelar",
        save: "Guardar",
        delete: "Eliminar",
        edit: "Editar",
        close: "Cerrar",
        back: "Volver",
        search: "Buscar",
        loading: "Cargando…",
        empty: "Sin datos",
        actions: "Acciones",
        yes: "Sí",
        no: "No",
        all: "Todas",
        download: "Descargar",
        share: "Compartir",
        share_app: "Compartir app",
        view_all_stores: "Ver todas las tiendas",
        my_store: "Mi tienda",
        change_password: "Cambiar contraseña",
        new_password: "Nueva contraseña",
        store: "Tienda",
        district: "Distrito / Región",
        city: "Ciudad",
        score: "Calificación",
        status: "Estado",
        category: "Categoría",
        question: "Pregunta",
        weight: "Peso",
        criticality: "Criticidad",
        assigned_store: "Tienda asignada",
        none: "Ninguna",
        copy: "Copiar",
        copied: "Copiado",
        clear: "Limpiar",
        active: "Activo",
        inactive: "Inactivo",
        next: "Siguiente",
        previous: "Anterior",
        uploading: "Subiendo…",
        processing: "Procesando…",
        upload_error: "Error al subir archivo",
        take_photo: "Tomar foto",
        attach_file: "Adjuntar archivo",
        upload_excel: "Cargar Excel",
        files_uploaded: "{{n}} archivo(s) subido(s)",
        file_too_big: "{{name}} excede 5MB, puede tardar en subir.",
        file_empty: "El archivo no contiene filas",
        file_read_error: "No se pudo leer el archivo: {{reason}}",
        loading_audit: "Cargando auditoría…",
        loading_dashboard: "Cargando dashboard…",
        loading_summary: "Cargando resumen…",
      },
      roles: {
        admin: "Administrador",
        auditor: "Auditor",
        gerente: "Gerente",
        direccion: "Dirección",
      },
      nav: {
        dashboard: "Dashboard",
        assignments: "Tareas",
        stores: "Tiendas",
        checklist: "Checklist",
        audits: "Auditorías",
        findings: "Hallazgos",
        followup: "Seguimiento",
        games: "Juego",
        flow: "Flujo",
        users: "Usuarios",
        permissions: "Permisos",
      },
      permissions: {
        title: "Permisos por rol",
        subtitle: "Configura qué puede ver y hacer cada rol. Los permisos del Administrador no se pueden cambiar (siempre tiene acceso total).",
        permission: "Permiso",
        immutable: "Bloqueado",
        save_per_role_hint: "Guarda los cambios por rol →",
        saved: "Permisos del rol {{role}} guardados",
        reset: "Restablecer cambios",
        role_admin: "Administrador",
        role_auditor: "Auditor",
        role_gerente: "Gerente",
        role_direccion: "Dirección",
        k_view_dashboard: "Ver Dashboard",
        d_view_dashboard: "Acceso a la página principal con KPIs y gráficos.",
        k_view_stores: "Ver Tiendas",
        d_view_stores: "Lista y catálogo de tiendas.",
        k_edit_stores: "Editar Tiendas",
        d_edit_stores: "Crear, modificar o borrar tiendas.",
        k_view_checklist: "Ver Checklist",
        d_view_checklist: "Ver las preguntas y plantillas configuradas.",
        k_edit_checklist: "Editar Checklist",
        d_edit_checklist: "Crear/editar preguntas, plantillas y categorías.",
        k_view_audits: "Ver Auditorías",
        d_view_audits: "Histórico de auditorías y reportes.",
        k_start_audit: "Iniciar Auditoría",
        d_start_audit: "Empezar una auditoría desde la página de tiendas.",
        k_edit_audit: "Editar Auditoría",
        d_edit_audit: "Continuar o reabrir auditorías para corregir respuestas.",
        k_delete_audit: "Eliminar Auditoría",
        d_delete_audit: "Borrar reportes individuales (alto riesgo).",
        k_view_findings: "Ver Hallazgos",
        d_view_findings: "Lista de hallazgos pendientes y completados.",
        k_edit_findings: "Editar Hallazgos",
        d_edit_findings: "Asignar responsable, cambiar estado y agregar notas.",
        k_share_finding: "Compartir Hallazgo",
        d_share_finding: "Generar un enlace público para que el responsable lo actualice.",
        k_delete_finding: "Eliminar Hallazgo",
        d_delete_finding: "Borrar hallazgos individuales.",
        k_send_sms: "Enviar SMS",
        d_send_sms: "Enviar SMS al responsable desde un hallazgo (Twilio).",
        k_view_followup: "Ver Seguimiento",
        d_view_followup: "Pizarra con vencimientos y atrasos.",
        k_view_users: "Ver Usuarios",
        d_view_users: "Lista de usuarios y sus roles.",
        k_manage_users: "Gestionar Usuarios",
        d_manage_users: "Crear, editar, deshabilitar usuarios.",
        k_view_my_findings: "Ver Mis hallazgos",
        d_view_my_findings: "Widget de hallazgos asignados al usuario en el dashboard.",
        k_manage_permissions: "Gestionar Permisos",
        d_manage_permissions: "Acceder a esta página y editar la matriz de roles.",
        k_view_assignments: "Ver Tareas y Auditorías",
        d_view_assignments: "Ver las tareas y auditorías asignadas.",
        k_manage_assignments: "Gestionar Tareas y Auditorías",
        d_manage_assignments: "Crear, asignar, reasignar, cambiar fechas y cancelar tareas.",
      },
      logic: {
        title: "Lógica condicional",
        add_logic: "Agregar lógica",
        flagged_title: "Respuestas marcadas (problema)",
        flagged_hint: "Qué respuestas cuentan como hallazgo/bandera para esta pregunta.",
        use_default: "Usar predeterminado",
        if_answer: "Si la respuesta es",
        then: "Entonces",
        add_rule: "Agregar regla",
        trg_instruction: "Mostrar instrucción / mensaje al auditor",
        trg_instruction_ph: "Ej. Es importante que el gerente reciba los resultados de la inspección.",
        trg_require_photo: "Exigir foto",
        trg_require_comment: "Exigir comentario",
        trg_followup: "Pregunta de seguimiento",
        trg_followup_ph: "Ej. ¿Cuál es la causa raíz?",
        trg_followup_required: "Obligatoria",
        trg_create_action: "Crear acción correctiva (TO DO)",
        trg_action_severity: "Severidad",
        trg_action_assignee: "Responsable",
        trg_action_assignee_ph: "Ej. Gerente de tienda",
        trg_action_due: "Vence en (días)",
        trg_notify: "Notificar (campanita)",
        trg_block_close: "Bloquear cierre hasta resolver",
        save: "Guardar lógica",
        saved: "Lógica guardada",
        error: "No se pudo guardar la lógica",
        tpl_insert: "Insertar plantilla",
        tpl_save: "Guardar como plantilla",
        tpl_saved: "Plantilla guardada",
        tpl_empty: "Escribe una instrucción primero",
        tpl_none: "Sin plantillas aún",
        trg_manual: "Manual / archivo de referencia",
        trg_manual_hint: "Se muestra debajo de la pregunta cuando aplique la regla.",
        trg_manual_upload: "Adjuntar manual",
        preview_title: "Vista previa",
        preview_when: "Al responder {{answers}}, el auditor verá:",
        no_answer_options: "Este tipo de respuesta (texto, número, media, firma) no usa reglas por respuesta.",
      },
      games: {
        module: "Capacitación / Juego",
        subtitle: "Juego estilo '100 Mexicanos Dijeron' para capacitar a tu equipo con los estándares de Canales.",
        new_game: "Nuevo juego",
        edit_game: "Editar juego",
        tab_games: "Juegos",
        tab_history: "Historial",
        questions: "preguntas",
        inactive: "Inactivo",
        active: "Activo (visible para jugar)",
        play: "Jugar",
        copy_link: "Copiar link para proyectar",
        link_copied: "Link copiado",
        confirm_delete: "¿Eliminar el juego \"{{title}}\"?",
        deleted: "Juego eliminado",
        no_matches: "Aún no hay partidas jugadas.",
        col_game: "Juego",
        col_result: "Resultado",
        col_winner: "Ganador",
        col_host: "Anfitrión",
        col_date: "Fecha",
        empty_title: "Crea tu primer juego",
        empty_desc: "Arma un tablero estilo Family Feud con preguntas y respuestas, o genéralas con IA desde tu checklist.",
        saved: "Juego guardado",
        save_error: "No se pudo guardar el juego",
        title_required: "Escribe un título",
        need_one_question: "Agrega al menos una pregunta con respuestas",
        editor_hint: "Cada pregunta lleva hasta 8 respuestas con puntos (suman ~100).",
        game_title: "Título",
        game_description: "Descripción",
        ai_title: "Generar con IA",
        ai_desc: "Crea preguntas y respuestas a partir del manual/checklist de Canales.",
        ai_count: "Cantidad",
        ai_topic: "Tema (opcional)",
        ai_topic_ph: "Ej. imagen, exhibición, MOTO",
        ai_generate: "Generar",
        ai_empty: "La IA no devolvió preguntas, intenta de nuevo",
        ai_added: "Se agregaron {{count}} preguntas",
        ai_error: "No se pudo generar con IA",
        question_ph: "Escribe la pregunta de encuesta…",
        answer_ph: "Respuesta",
        points: "Pts",
        add_answer: "Agregar respuesta",
        add_question: "Agregar pregunta",
        not_found: "Juego no encontrado",
        not_available: "Este juego no está disponible",
        brand_tagline: "Capacitación Canales",
        team_a: "Equipo A",
        team_b: "Equipo B",
        start_game: "¡Comenzar!",
        tie: "Empate",
        winner: "Ganador",
        save_match: "Guardar resultado",
        match_saved: "Resultado guardado",
        play_again: "Jugar de nuevo",
        back_to_games: "Volver a juegos",
        question: "Pregunta",
        round_points: "Puntos en juego",
        strike: "Strike",
        reset_round: "Reiniciar ronda",
        next: "Siguiente",
        finish: "Terminar",
        show_key: "Ver respuestas (anfitrión)",
        hide_key: "Ocultar respuestas",
        decoys_title: "Respuestas erróneas (señuelos)",
        decoys_hint: "No aparecen en el tablero. Si un equipo elige una, pierde el turno.",
        decoy_ph: "Respuesta incorrecta pero creíble",
        add_decoy: "Agregar señuelo",
        live: {
          title: "Partida en vivo",
          play_live: "En vivo",
          setup_hint: "Dos tiendas juegan desde su propia pantalla. Tú conduces como anfitrión.",
          store_a: "Tienda A",
          store_b: "Tienda B",
          turn_mode: "Modo de turnos",
          mode_strikes: "Clásico (3 strikes + robo)",
          mode_strikes_desc: "El equipo acumula puntos; a los 3 errores el otro equipo puede robar la ronda.",
          mode_instant: "Rápido (1 error = cambio de turno)",
          mode_instant_desc: "Cada acierto suma; al primer error pasa el turno al otro equipo.",
          create_room: "Crear sala",
          create_error: "No se pudo crear la sala",
          join_at: "Únete en",
          room_code: "Código de sala",
          copy_join_link: "Copiar link de la sala",
          start_match: "Iniciar partida",
          waiting_teams: "Puedes iniciar cuando ambas tiendas estén conectadas.",
          close_room: "Cerrar sala",
          connected: "✓ Conectado",
          waiting: "Esperando…",
          join_title: "Unirse a la partida",
          join_hint: "Escribe el código que muestra el anfitrión.",
          enter: "Entrar",
          enter_code: "Escribe el código de la sala",
          room_not_found: "Sala no encontrada",
          try_another: "Probar otro código",
          pick_your_store: "Elige tu tienda",
          tap_to_join: "Toca para unirte",
          slot_taken_hint: "Ya conectado (puedes reconectar)",
          you_are: "Eres",
          waiting_host: "Esperando a que el anfitrión inicie…",
          choose_answer: "Elige una respuesta",
          your_turn: "¡Es tu turno! Elige una respuesta",
          other_turn: "Turno de {{team}}",
          turn_of: "Turno de {{team}}",
          round_over: "Ronda terminada — espera al anfitrión",
          steal_banner: "¡Robo! {{team}} tiene una oportunidad",
          match_saved: "Resultado guardado en el historial",
          new_match: "Nueva partida",
          host_key_hint: "Puntos verdes = correctas · rojos = señuelos (solo tú los ves).",
          pick_error: "No se pudo registrar tu respuesta",
        },
      },
      response_types: {
        canales: "Canales (Sí/No/En proceso/Advertencia/Multa)",
        good_fair_poor: "Bueno / Regular / Malo",
        pass_fail: "Aprobado / Reprobado",
        compliant: "Cumple / No cumple",
        safe_risk: "Seguro / En riesgo",
        text: "Texto",
        number: "Número",
        media: "Foto / Media",
        signature: "Firma",
      },
      assignments: {
        overline: "Tareas y Auditorías",
        title: "Tareas y Auditorías Asignadas",
        subtitle: "Asigna auditorías y tareas a tu equipo, da seguimiento y revisa resultados.",
        my_tasks_title: "Mis tareas",
        my_tasks_subtitle: "Tareas y auditorías asignadas a ti.",
        new_assignment: "Nueva tarea",
        tab_list: "Lista",
        tab_schedule: "Agenda semanal",
        all: "Todas",
        filter_auditor: "Auditor",
        filter_store: "Tienda",
        filter_status: "Estado",
        filter_category: "Categoría",
        filter_type: "Tipo",
        clear_filters: "Limpiar filtros",
        search_ph: "Buscar tarea, tienda o auditor…",
        status_pending: "Pendiente",
        status_in_progress: "En progreso",
        status_completed: "Completada",
        status_overdue: "Vencida",
        status_cancelled: "Cancelada",
        priority: "Prioridad",
        priority_low: "Baja",
        priority_medium: "Media",
        priority_high: "Alta",
        type: "Tipo",
        type_store_audit: "Auditoría de tienda",
        type_task_checklist: "Checklist de tareas",
        auditor: "Auditor responsable",
        store: "Tienda",
        categories: "Categorías",
        assigned_date: "Fecha de asignación",
        due_date: "Fecha límite",
        instructions: "Instrucciones",
        comment: "Comentarios",
        attachments: "Archivos adjuntos",
        task_title: "Título de la tarea",
        due: "Fecha límite",
        assigned: "Asignada",
        no_due: "Sin fecha límite",
        start_task: "Iniciar tarea",
        open_audit: "Abrir auditoría",
        continue_audit: "Continuar auditoría",
        view: "Ver",
        view_detail: "Ver detalle",
        complete_task: "Completar tarea",
        reassign: "Reasignar",
        cancel_task: "Cancelar tarea",
        delete_task: "Eliminar",
        back: "Volver a tareas",
        details: "Detalles",
        history_title: "Historial de la tarea",
        comments_title: "Conversación",
        add_comment: "Enviar",
        comment_ph: "Escribe un comentario…",
        no_comments: "Aún no hay comentarios.",
        attach_photo: "Adjuntar foto",
        checklist_title: "Checklist",
        item_complete: "Completo",
        item_incomplete: "Incompleto",
        item_na: "N/A",
        item_pending: "Pendiente",
        item_comment_ph: "Comentario…",
        add_photo: "Agregar foto",
        add_another_photo: "Agregar otra foto",
        progress: "Progreso",
        reassign_title: "Reasignar tarea",
        reassign_to: "Nuevo auditor",
        reassign_note: "Nota (opcional)",
        confirm_reassign: "Reasignar",
        reassigned_from: "Reasignada de {{from}} a {{to}}",
        create_title: "Nueva tarea o auditoría",
        create_subtitle: "Asigna una auditoría de tienda o un checklist de tareas a un auditor.",
        choose_type: "Tipo de asignación",
        choose_type_audit: "Auditoría de tienda",
        choose_type_audit_desc: "Reutiliza el motor de auditoría con las categorías elegidas.",
        choose_type_task: "Checklist de tareas",
        choose_type_task_desc: "Lista de tareas internas (herramientas, conocimiento, semanal).",
        choose_categories: "Categorías a auditar",
        choose_categories_hint: "Selecciona las plantillas que el auditor debe completar.",
        checklist_items: "Elementos del checklist",
        add_item: "Agregar elemento",
        item_ph: "Ej. Revisar herramientas",
        remove: "Quitar",
        attach_files: "Adjuntar archivos o fotos",
        save: "Crear tarea",
        empty_title: "Sin tareas todavía",
        empty_hint: "Crea la primera tarea para asignar auditorías a tu equipo.",
        empty_auditor: "No tienes tareas asignadas.",
        no_results: "No hay tareas con estos filtros.",
        schedule_title: "Agenda semanal del auditor",
        week: "Semana",
        this_week: "Esta semana",
        next_week: "Próxima semana",
        prev_week: "Semana anterior",
        col_auditor: "Auditor",
        col_store: "Tienda",
        col_assignment: "Asignación",
        col_status: "Estado",
        no_schedule: "No hay asignaciones esta semana.",
        cancel_confirm: "¿Cancelar esta tarea? El historial se conserva.",
        delete_confirm: "¿Eliminar esta tarea permanentemente?",
        result_score: "Resultado",
        time_spent: "Tiempo utilizado",
        completed_by: "Completada por",
        created_by: "Creada por",
        not_started: "Sin iniciar",
        instructions_from: "Instrucciones del admin",
        no_instructions: "Sin instrucciones.",
        no_attachments: "Sin archivos adjuntos.",
        toast_created: "Tarea creada y asignada",
        toast_updated: "Tarea actualizada",
        toast_reassigned: "Tarea reasignada",
        toast_started: "Tarea iniciada",
        toast_completed: "Tarea completada",
        toast_cancelled: "Tarea cancelada",
        toast_deleted: "Tarea eliminada",
        toast_comment: "Comentario enviado",
        toast_item_saved: "Elemento actualizado",
        toast_error: "Ocurrió un error",
        complete_blocked: "Resuelve todos los elementos del checklist antes de completar.",
        need_auditor: "Selecciona un auditor.",
        need_store: "Selecciona una tienda.",
        need_category: "Selecciona al menos una categoría.",
        need_items: "Agrega al menos un elemento al checklist.",
        store_history: "Historial de auditorías y tareas",
        minutes_short: "min",
        drag_hint: "Arrastra una asignación a otro auditor para reasignarla.",
      },
      login: {
        title: "Iniciar sesión",
        subtitle: "Acceso operativo · Mueblería Canales",
        hero_title: "Auditoría operativa,",
        hero_title_2: "hecha en piso de venta.",
        hero_desc: "Captura evidencia, califica con ponderación, genera hallazgos y reportes PDF firmados — todo en un mismo flujo guiado para tus tiendas.",
        forgot: "¿Olvidaste tu contraseña? Pide al administrador que la reestablezca.",
        bad_credentials: "Correo o contraseña inválidos",
        signing_in: "Entrando…",
        badge: "v2.1 · Modo operativo",
        bullet_1: "Auditorías guiadas, fotos, hallazgos y firmas en un solo flujo.",
        bullet_2: "Reportes PDF firmados con QR para compartir con Dirección.",
        bullet_3: "Disponible en teléfono, iPad y computadora.",
      },
      dashboard: {
        title: "Dashboard ejecutivo",
        greeting: "Hola, {{name}}.",
        subtitle_global: "Visión operativa de tus tiendas.",
        subtitle_mine: "Indicadores de tu tienda asignada.",
        no_audits: "Aún no tienes auditorías. Empieza la primera desde el catálogo de tiendas.",
        hero_paragraph: "Indicadores clave, semáforo de calificaciones y ranking en tiempo real.",
        kpi_audits: "Auditorías",
        kpi_closed: "Cerradas",
        kpi_in_progress: "En curso",
        kpi_avg: "Score promedio",
        kpi_stores: "Tiendas",
        kpi_findings: "Hallazgos",
        kpi_views: "Vistas reportes",
        start_audit: "Iniciar auditoría",
        view_history: "Ver historial",
        recent_findings_subtitle: "Acciones que requieren atención",
        trend: "Tendencia mensual",
        trend_subtitle: "Score promedio por mes",
        semaphore: "Distribución semáforo",
        semaphore_subtitle: "Tiendas por desempeño",
        ranking: "Ranking de tiendas",
        ranking_subtitle: "Top auditorías cerradas",
        recent_findings: "Hallazgos recientes",
        share_activity_title: "¿Quién está leyendo los reportes?",
        share_activity_subtitle: "Los enlaces públicos que has compartido y cuántas veces se han abierto.",
        share_empty: "Aún no hay reportes compartidos. Abre cualquier auditoría cerrada y presiona Compartir.",
      },
      stores: {
        title: "Catálogo de tiendas",
        hero_title: "Selecciona la tienda que vas a auditar.",
        hero_desc: "Importa tu Excel, busca por nombre o ciudad, y empieza el flujo guiado en un toque.",
        empty_title: "No hay tiendas que mostrar",
        empty_admin: "Importa tu catálogo desde Excel o agrega una tienda manualmente para comenzar.",
        empty_other: "Pídele al Admin que cargue el catálogo de tiendas.",
        new_store: "Nueva tienda",
        template: "Plantilla",
        import: "Importar",
        import_excel: "Importar Excel",
        audit: "Auditar",
        readonly: "Solo lectura",
        no_audited: "Sin auditar",
        search_placeholder: "Buscar tienda, distrito o ciudad…",
        filter_all: "Todas",
        filter_green: "Verde",
        filter_yellow: "Amarillo",
        filter_red: "Rojo",
        filter_pending: "Sin auditar",
        confirm_delete: '¿Eliminar "{{name}}"?',
        added: "Tienda agregada",
        required: "Tienda es requerida",
        imported: "Importadas {{n}} tiendas",
        headers_error: "Encabezados esperados: Tienda, Distrito, Ciudad",
        audit_started: "Auditoría iniciada en {{store}}",
        audit_start_error: "No se pudo iniciar la auditoría",
        role_error: "Solo Auditor o Admin pueden iniciar auditorías",
        edit_score: "Editar calificación",
        edit_score_hint: "Ajusta la calificación y el semáforo manualmente.",
        score_label: "Calificación (0–100)",
        semaphore_label: "Semáforo",
        semaphore_auto: "Automático",
        score_invalid: "Ingresa una calificación válida entre 0 y 100",
        score_updated: "Calificación actualizada",
        score_error: "No se pudo actualizar la calificación",
      },
      audit: {
        in_field: "Auditoría en campo",
        loading: "Cargando auditoría…",
        progress: "Progreso: {{answered}} de {{total}} preguntas",
        close: "Cerrar auditoría",
        closed: "Cerrada",
        no_checklist: "No hay checklist cargado",
        no_checklist_desc: "Importa un Excel con las columnas: Plantilla, Categoria, Pregunta, Peso, Criticidad",
        upload_checklist: "Cargar checklist",
        cumple: "Sí",
        no_cumple: "No",
        na: "No Aplica",
        yes: "Sí",
        no: "No",
        en_proceso: "En proceso",
        warning: "Advertencia",
        multa: "Multa",
        good: "Bueno",
        fair: "Regular",
        poor: "Malo",
        pass: "Aprobado",
        fail: "Reprobado",
        compliant: "Cumple",
        non_compliant: "No cumple",
        safe: "Seguro",
        at_risk: "En riesgo",
        text_answer_ph: "Escribe la respuesta…",
        number_answer_ph: "Ingresa un número…",
        media_answer: "Subir foto",
        signature_save: "Guardar firma",
        signature_saved: "Firma guardada",
        signature_empty: "Dibuja la firma primero",
        view_signature: "Ver firma",
        manual: "Manual de referencia",
        view_manual: "Ver manual",
        comment: "Comentario",
        photo: "Foto",
        comment_required: "Describe el incumplimiento (obligatorio)…",
        comment_optional: "Comentario opcional…",
        photo_required: "Foto evidencia (obligatoria)",
        photo_optional: "Foto opcional",
        auditor_label: "Auditor",
        weight_label: "Peso",
        criticality_label: "Criticidad",
        saving: "Guardando…",
        comment_saved: "Comentario guardado",
        save_error: "No se pudo guardar la respuesta",
        audit_closed: "Auditoría cerrada",
        confirm_close: "Faltan {{n}} preguntas por responder. ¿Cerrar de todas formas?",
        checklist_headers_error: "Encabezados esperados: Plantilla, Categoria, Pregunta, Peso, Criticidad",
        checklist_updated: "Checklist actualizado: {{n}} preguntas",
        template_label: "Plantilla",
        no_questions_in_template: "Esta plantilla aún no tiene preguntas. Agrega o importa preguntas en la sección Checklist.",
        comment_required_error: "Comentario obligatorio para esta respuesta.",
        followup_required_error: "Debes responder esta pregunta de seguimiento.",
        photo_required_error: "Foto obligatoria para esta respuesta.",
        evidence_banner_title: "Hay {{n}} preguntas con evidencia incompleta.",
        evidence_banner_desc: "Antes de cerrar la auditoría, completa la evidencia obligatoria (comentario, foto y/o pregunta de seguimiento) en las respuestas marcadas.",
        evidence_jump: "Saltar a la primera pendiente",
        evidence_missing_close: "Falta evidencia (foto + nota) en {{n}} pregunta(s). Empieza con: \"{{q}}…\"",
        next_unanswered: "Siguiente sin responder",
        go_top: "Ir arriba",
      },
      cover: {
        title: "Iniciar auditoría",
        subtitle: "Completa la portada antes de empezar.",
        auditor_name: "Nombre del auditor",
        manager_name: "Nombre del gerente en turno",
        manager_placeholder: "Ej. José López",
        address: "Dirección",
        address_placeholder: "Calle, número, ciudad",
        date: "Fecha de auditoría",
        audit_number: "Número de auditoría",
        audit_number_hint: "Se generará automáticamente, puedes editarlo.",
        choose_template: "Selecciona la plantilla a auditar",
        choose_template_multi: "Selecciona una o varias plantillas para auditar",
        template_subtitle: "Cada plantilla tiene su propio set de preguntas. Puedes elegir varias y hacerlas en una sola auditoría.",
        new_template: "Nueva plantilla",
        new_template_dialog: "Crear plantilla",
        template_name: "Nombre de la plantilla",
        template_description: "Descripción (opcional)",
        questions_count: "{{n}} preguntas",
        no_questions: "Sin preguntas",
        start: "Empezar auditoría",
        start_audit_with: "Empezar auditoría · {{template}}",
        start_audit_multi: "Empezar auditoría · {{n}} plantillas",
        manager_required: "Captura el nombre del gerente en turno",
        template_required: "Selecciona una plantilla",
        template_created: "Plantilla creada",
        template_exists: "Ya existe una plantilla con ese nombre",
        delete_template_confirm: "¿Eliminar la plantilla \"{{name}}\"?",
      },
      multa: {
        title: "Aplicar multa",
        subtitle: "Captura el monto, la justificación y a quién notificar.",
        amount: "Monto de la multa",
        amount_placeholder: "0.00",
        currency: "USD",
        reason_label: "Justificación",
        reason_placeholder: "Explica por qué se aplica la multa…",
        ai_generate: "Generar con IA",
        ai_regenerate: "Regenerar",
        ai_generating: "Generando con Claude…",
        ai_error: "No se pudo generar con IA",
        recipients_label: "Notificar a",
        no_recipients_available: "Sin auditores disponibles para notificar.",
        no_recipients_selected: "Selecciona al menos un destinatario",
        save_and_notify: "Guardar y notificar",
        saved_only: "Guardar sin notificar",
        notified: "Notificación enviada a {{n}} usuario(s)",
        observation_label: "Observación adicional (opcional)",
        observation_placeholder: "Contexto extra para la IA…",
      },
      notifications: {
        title: "Notificaciones",
        empty: "No tienes notificaciones.",
        from: "De",
        store: "Tienda",
        amount: "Monto",
        mark_read: "Marcar como leída",
        mark_all_read: "Marcar todas como leídas",
        unread: "Sin leer",
        unread_n: "{{n}} sin leer",
        by: "por",
        showing_n: "{{n}} mostradas",
        view_all: "Ver todo",
        kind_started: "TO DO en curso",
        kind_done: "TO DO completado",
        kind_cannot_do: "No pude hacer",
        kind_reopened: "Reabierto a TO DO",
        kind_note: "Comentario actualizado",
        kind_photo: "Foto agregada",
        kind_assignment_assigned: "Tarea asignada",
        kind_assignment_reassigned: "Tarea reasignada",
        kind_assignment_started: "Tarea iniciada",
        kind_assignment_completed: "Tarea completada",
        kind_assignment_comment: "Comentario en tarea",
        kind_audit_flag: "Respuesta marcada en auditoría",
        page_title: "Historial de actividad",
        page_subtitle: "Cada movimiento en un link TO DO queda registrado aquí.",
        total: "Total",
        showing: "Mostrando",
        search_ph: "Buscar por descripción, autor…",
        filter_all: "Todos los tipos",
        filter_store_ph: "Filtrar por tienda…",
        only_unread: "Solo sin leer",
        empty_filtered: "No hay notificaciones que coincidan con los filtros.",
        pagination: "Mostrando {{from}}–{{to}} de {{total}}",
        view_photo: "Ver foto",
        export_csv: "Exportar CSV",
        exported: "CSV descargado",
      },
      audits_list: {
        title: "Histórico de auditorías",
        subtitle: "Continúa una en curso o consulta el resumen de las cerradas.",
        in_progress: "En curso",
        empty: "No hay auditorías. Inicia una desde el catálogo de tiendas.",
        view_summary: "Resumen",
        continue: "Continuar",
        auditor: "Auditor",
        start: "Inicio",
        delete: "Eliminar",
        confirm_delete: "¿Eliminar la auditoría de \"{{store}}\" del {{date}}?\n\nSe borrarán respuestas, hallazgos y notificaciones asociadas. Esta acción no se puede deshacer.",
        deleted: "Auditoría eliminada",
        delete_error: "No se pudo eliminar la auditoría",
        edit_report: "Editar",
        reopen: "Reabrir auditoría",
        confirm_reopen: "Reabrir la auditoría de \"{{store}}\". Volverá al modo de edición — podrás cambiar respuestas y agregar evidencia.",
        reopened: "Auditoría reabierta",
      },
      flow: {
        title: "Flujo Operativo",
        hero_title: "10 pasos guiados.",
        hero_desc: "Sigue el flujo en orden para garantizar una auditoría completa, con evidencia y firmas.",
        continue_active: "Continuar auditoría activa",
        go_step: "Ir al paso",
        steps: {
          s1_title: "Selecciona tienda",
          s1_desc: "Elige la tienda objetivo en el catálogo. Quedará marcada como tienda activa.",
          s2_title: "Carga checklist",
          s2_desc: "Importa el checklist (categoría, pregunta, peso, criticidad) o usa el catálogo cargado.",
          s3_title: "Inicia auditoría",
          s3_desc: "Desde la tienda seleccionada, presiona 'Auditar' para abrir el flujo en campo.",
          s4_title: "Auditoría en campo",
          s4_desc: "Responde Cumple / No Cumple / No Aplica. Captura fotos y comentarios.",
          s5_title: "Genera hallazgos",
          s5_desc: "Cada No Cumple genera un hallazgo automático listo para asignar responsable.",
          s6_title: "Calcula calificación",
          s6_desc: "El score ponderado se calcula automáticamente y aplica el semáforo.",
          s7_title: "Asigna responsables",
          s7_desc: "En Hallazgos define responsable, fecha compromiso y severidad.",
          s8_title: "Seguimiento gerente",
          s8_desc: "Gerente actualiza estado de las acciones correctivas.",
          s9_title: "Resumen y firmas",
          s9_desc: "Captura nombre y firma de Gerente y Regional. Genera el resumen IA.",
          s10_title: "Genera PDF",
          s10_desc: "Descarga el reporte ejecutivo firmado en PDF.",
        },
      },
      checklist_page: {
        title: "Preguntas del checklist",
        subtitle: "Define las preguntas de auditoría con su categoría, peso ponderado y criticidad.",
        new_question: "Nueva pregunta",
        edit_question: "Editar pregunta",
        response_type: "Tipo de respuesta",
        response_type_hint: "Define qué opciones o campo de captura usa esta pregunta.",
        edit_template: "Editar plantilla",
        drag_to_reorder: "Arrástrame para reordenar",
        template_color: "Color",
        template_color_custom: "Personalizado",
        delete_template: "Eliminar plantilla",
        template_deleted: "Plantilla eliminada",
        confirm_delete_template: "¿Eliminar la plantilla \"{{name}}\"?",
        confirm_delete_template_force: "\"{{name}}\" está en uso: {{checklist}} pregunta(s) del checklist y {{audits}} auditoría(s). Si continúas, las preguntas conservarán este nombre como texto pero no aparecerán en el filtro. ¿Continuar?",
        empty: "Sin preguntas. Importa un Excel o agrega manualmente.",
        confirm_delete: "¿Eliminar pregunta?",
        added: "Pregunta agregada",
        updated: "Pregunta actualizada",
        required: "Categoría y pregunta requeridas",
        category_placeholder: "Sala, Recámara, Financiamiento…",
        translate: "Traducir a {{lang}}",
        translate_tooltip: "Genera automáticamente la versión en el idioma activo con IA. Las traducciones se guardan y se reutilizan.",
        translating: "Traduciendo…",
        translated: "{{n}} traducidas (de {{total}})",
        translate_error: "No se pudo traducir. Intenta de nuevo o revisa los logs del servidor.",
        translate_error_http: "No se pudo traducir (HTTP {{code}}). Intenta de nuevo.",
        pdf_photo: "PDF / Foto",
        extracting: "Extrayendo…",
        pdf_tooltip: "Extrae preguntas con IA desde PDF o foto",
      },
      summary: {
        title: "Resumen Final",
        ai_title: "Resumen ejecutivo IA",
        ai_subtitle: "Narrativa automática",
        ai_placeholder: "Genera un resumen automático con Claude Sonnet 4.5 o escribe el tuyo…",
        generate_ai: "Generar resumen",
        regenerate_ai: "Regenerar",
        gerente_name: "Gerente de tienda",
        regional_name: "Regional / Auditor",
        save_signatures: "Guardar firmas",
        gerente_signature: "Firma del gerente",
        regional_signature: "Firma regional / auditor",
        sign_here: "Firma aquí",
        download_pdf: "Descargar PDF",
        category_subtotals: "Subtotales por categoría",
        attachments: "Archivos adjuntos",
        share_report: "Compartir reporte",
        share_desc: "Cualquier persona con este enlace podrá ver el reporte de solo lectura — sin necesidad de iniciar sesión.",
        no_link: "Aún no hay enlace.\nElige caducidad abajo y genera.",
        expiration: "Caducidad del enlace",
        no_expiry: "Sin caducidad",
        days_7: "7 días",
        days_30: "30 días",
        days_90: "90 días",
        generate_link: "Generar enlace",
        update_expiry: "Actualizar caducidad",
        revoke: "Revocar enlace",
        confirm_revoke: "¿Revocar el enlace público? Quien tenga el link dejará de poder verlo.",
        link_revoked: "Enlace revocado",
        link_copied: "Enlace copiado al portapapeles",
        copy_failed: "No se pudo copiar el enlace",
        views: "Visitas",
        last_viewed: "Última: {{when}}",
        not_viewed: "Aún no se ha abierto",
        expires_on: "Expira: {{date}}",
        in_n_days: "en {{n}} día(s)",
        tip_qr: "Tip: El QR también queda impreso en la última página del PDF.",
        link_error: "No se pudo generar el enlace",
        ai_success: "Resumen IA generado",
        ai_error: "No se pudo generar el resumen IA",
        signatures_saved: "Firmas y resumen guardados",
      },
      users: {
        title: "Usuarios",
        subtitle: "Solo el administrador puede crear, editar o desactivar usuarios.",
        new_user: "Nuevo usuario",
        edit_user: "Editar usuario",
        delete_confirm: "¿Eliminar a {{name}}? Esta acción es permanente.",
        locked: "Bloqueado",
        locked_until_tooltip: "Cuenta bloqueada por intentos fallidos hasta {{when}}",
        unlock: "Desbloquear",
        unlock_confirm: "¿Desbloquear la cuenta de {{name}}?",
        unlocked: "Cuenta de {{name}} desbloqueada",
        reset_password: "Restablecer contraseña",
        reset_password_prompt: "Escribe la nueva contraseña para {{name}} (mínimo 6 caracteres). El usuario podrá iniciar sesión inmediatamente.",
        password_too_short: "La contraseña debe tener al menos 6 caracteres",
        password_reset: "Contraseña de {{name}} restablecida",
        leave_password_blank: "Deja en blanco para no cambiar la contraseña.",
        no_assignment: "Sin tienda asignada (acceso global)",
        phone_label: "Teléfono (con código de país)",
        phone_placeholder: "+1 555 123 4567",
        phone_hint: "Formato internacional con +. Se usa para recibir SMS automáticos.",
        phone_col: "Teléfono",
        optin_title: "Solicitudes de Opt-In SMS",
        optin_hint: "Personas registradas en el formulario público /sms-optin. Vincúlalas a un usuario para asignarles el teléfono.",
        optin_link: "Vincular",
        optin_select_user: "Selecciona usuario…",
        optin_linked_toast: "Teléfono vinculado al usuario",
        notifications_title: "Preferencias de notificación (SMS)",
        notifications_hint: "Activa qué eventos generan un SMS informativo a este usuario.",
        notif_multa: "Multa aplicada en auditoría",
        notif_to_do: "Me asignan un TO DO",
        notif_no_followup_24h: "TO DO sin seguimiento +24h",
        notif_reports: "Reporte listo (auditoría cerrada)",
        notif_alerts: "Alertas generales",
        notif_todo_completed: "TO DO completado desde checklist público",
        notif_assignment: "Tarea/auditoría asignada o reasignada a mí",
        notif_task_update: "Tarea que creo/superviso fue iniciada, completada o comentada",
        notif_overdue: "Un TO DO del que soy responsable (o superviso) venció su fecha compromiso",
        bulk_selected: "{{count}} seleccionado(s)",
        bulk_unlock: "Desbloquear",
        bulk_reset: "Resetear contraseñas",
        bulk_unlock_confirm: "¿Desbloquear {{count}} cuenta(s) seleccionada(s)?",
        bulk_unlocked: "{{count}} cuenta(s) desbloqueada(s)",
        bulk_reset_confirm: "¿Generar contraseñas temporales para {{count}} usuario(s)? Tendrán que cambiarla al iniciar sesión.",
        bulk_reset_done_title: "Contraseñas temporales generadas",
        bulk_reset_done_desc: "Estas contraseñas se muestran una sola vez. Cópialas o descárgalas ahora. Cada usuario tendrá que elegir su propia contraseña al iniciar sesión.",
        temp_password: "Contraseña temporal",
        download_csv: "Descargar CSV",
        select_all: "Seleccionar todo",
        select_row: "Seleccionar {{name}}",
      },
      force_pw: {
        title: "Elige tu nueva contraseña",
        subtitle: "Un administrador reinició tu acceso con una contraseña temporal. Por seguridad, escoge tu propia contraseña antes de continuar.",
        new: "Nueva contraseña",
        confirm: "Confirmar contraseña",
        submit: "Actualizar contraseña",
        logout: "Cerrar sesión",
        too_short: "La contraseña debe tener al menos 6 caracteres",
        mismatch: "Las contraseñas no coinciden",
        updated: "Contraseña actualizada",
        error: "No se pudo actualizar",
      },
      followup: {
        title: "Seguimiento Gerente",
        subtitle: "Actualiza el estado de cada acción y registra notas de seguimiento.",
        readonly_role: "(Solo lectura para tu rol.)",
        all_stores: "Todas las tiendas",
        responsible: "Responsable",
        commitment_date: "Fecha compromiso",
        notes: "Notas",
        notes_placeholder: "Observación, siguiente paso, evidencia…",
      },
      findings: {
        title: "Hallazgos",
        subtitle: "Cada No Cumple genera un hallazgo. Asigna responsable, fecha compromiso y severidad.",
        no_findings: "Sin hallazgos.",
        edit_finding: "Editar hallazgo",
        page_subtitle: "Incumplimientos detectados",
        search_placeholder: "Buscar por descripción, tienda o categoría…",
        all_status: "Todos",
        col_store: "Tienda",
        col_category: "Categoría",
        col_description: "Descripción",
        col_responsible: "Responsable",
        col_commitment: "Compromiso",
        col_severity: "Severidad",
        col_status: "Estado",
        col_action: "Acción",
        save_changes: "Guardar cambios",
        updated: "Hallazgo actualizado",
        notes_label: "Notas de seguimiento",
        confirm_delete: "¿Eliminar este hallazgo?",
        deleted: "Hallazgo eliminado",
        delete_error: "No se pudo eliminar el hallazgo",
        view_table: "Tabla",
        view_group: "Agrupar por tienda",
        group_total: "{{n}} hallazgos",
        send_checklist: "Enviar checklist SMS",
        col_photos: "Fotos",
        auto_refresh: "Auto-refresh 15s",
      },
      severity: {
        Alta: "Alta",
        Media: "Media",
        Baja: "Baja",
      },
      finding_status: {
        to_do: "Hacer",
        in_progress: "En curso",
        done: "Completo",
        cannot_do: "No puedo hacerlo",
        Pendiente: "Hacer",
        "En Proceso": "En curso",
        Cerrado: "Completo",
      },
      finding_share: {
        share_button: "Compartir hallazgo",
        title: "Compartir con responsable",
        subtitle: "Genera un enlace público para que el responsable pueda actualizar el estado, agregar notas y subir fotos sin necesidad de iniciar sesión.",
        generate: "Generar enlace",
        copy: "Copiar enlace",
        copied: "Enlace copiado",
        copy_failed: "No se pudo copiar el enlace",
        revoke: "Revocar enlace",
        confirm_revoke: "¿Revocar el enlace público? Quien lo tenga ya no podrá ver ni actualizar este hallazgo.",
        revoked: "Enlace revocado",
        link_active: "Enlace activo",
      },
      sms: {
        title: "Enviar SMS",
        subtitle: "Avisa a uno o varios responsables sobre esta tarea por mensaje de texto.",
        recipients: "Teléfonos destino",
        add_phone: "Agregar otro número",
        format_hint: "Incluye el código de país (ej. +1)",
        placeholder: "+1 5551234567",
        message: "Mensaje",
        message_placeholder: "Escribe el mensaje a enviar…",
        chars_left: "caracteres",
        send: "Enviar SMS ({{n}})",
        sending: "Enviando…",
        sent_n: "{{n}} SMS enviados",
        failed_n: "{{n}} fallaron",
        send_error: "No se pudo enviar (revisa Twilio o intenta de nuevo).",
        summary: "{{ok}} enviados · {{fail}} con error",
        not_configured: "SMS no configurado en este servidor.",
        button_label: "SMS",
        choose_team: "Elige del equipo",
        team_count: "{{n}} con teléfono",
        team_search: "Buscar por nombre, correo o rol…",
        team_empty: "Ningún usuario tiene teléfono registrado. Captúralo en Usuarios para poder seleccionarlo aquí.",
        max_recipients: "Máximo {{n}} destinatarios.",
      },
      group_sms: {
        title: "Compartir checklist",
        subtitle: "Un solo SMS con todos los pendientes agrupados por tienda.",
        subtitle_multichannel: "Envía la lista de pendientes por SMS, correo, Teams o copia el link. Todos abren la misma página interactiva.",
        total_findings: "{{n}} hallazgos en total",
        include_statuses: "Estados a incluir",
        findings_included: "TO DOs incluidos",
        empty: "No hay hallazgos para esta tienda.",
        no_selected: "Selecciona al menos un TO DO.",
        share_link_label: "Link con imagen TO DO",
        create_link: "Crear link con preview TO DO",
        creating_link: "Generando link…",
        link_ready: "Link listo con imagen TO DO",
        link_ready_short: "Listo",
        share_link_hint: "Este link muestra una imagen 'TO DO' cuando se comparte por SMS/WhatsApp. Genera el link para incluirlo en el mensaje.",
        share_link_hint_short: "Se generará automáticamente al enviar. Muestra la imagen 'TO DO' cuando el destinatario reciba el link.",
        send: "Enviar checklist ({{n}})",
        send_sms: "Enviar SMS ({{n}})",
        send_email: "Abrir correo ({{n}})",
        send_teams: "Compartir en Teams",
        send_whatsapp: "Abrir WhatsApp ({{n}})",
        whatsapp_label: "Números WhatsApp",
        whatsapp_default: "Abre el checklist interactivo:",
        whatsapp_opened: "Abriendo WhatsApp",
        whatsapp_multi_hint: "WhatsApp abre un chat por destinatario. Envía cada uno manualmente.",
        whatsapp_empty_hint: "Sin destinatarios se abre WhatsApp con el mensaje listo — tú eliges a quién enviárselo.",
        per_findings: "{{n}} hallazgos",
        reset_message: "Restaurar mensaje auto-generado",
        channel_label: "Canal",
        channel_sms: "SMS",
        channel_email: "Correo",
        channel_teams: "Teams",
        channel_whatsapp: "WhatsApp",
        channel_copy: "Copiar",
        pick_contacts: "Elige contactos",
        contacts_available: "{{n}} disponibles",
        no_emails: "Ningún usuario tiene correo registrado para este canal.",
        emails_label: "Correos destino",
        add_email: "Agregar otro correo",
        teams_placeholder: "usuario@empresa.com",
        optional_note: "Nota opcional (se antepone al link)",
        optional_note_placeholder: "Ej: Prioridad esta semana, revisar antes del viernes…",
        no_prefill_hint: "El link se envía solo — el destinatario abre la lista interactiva y actualiza cada TO DO (foto + comentario requeridos).",
        email_subject: "TO DO checklist",
        email_default_body: "Abre el checklist interactivo:",
        teams_default: "Abre el checklist interactivo:",
        email_opened: "Cliente de correo abierto",
        teams_opened: "Compartir en Teams",
        sms_default: "Checklist TO DO",
        copy_link: "Copiar link",
        copied: "Link copiado al portapapeles",
        copy_failed: "No se pudo copiar",
      },
      public_checklist: {
        overline: "Canales · TO DO Checklist",
        title: "Pendientes de tienda",
        count: "{{n}} pendientes",
        by: "Enviado por",
        empty: "No hay pendientes en este checklist.",
        responsible: "Responsable",
        no_responsible: "Sin asignar",
        open_item: "Abrir para actualizar",
        load_error: "No se pudo cargar el checklist.",
        not_found: "Checklist no disponible",
        progress: "Progreso",
        rule_hint: "Al marcar cualquier estado distinto a 'To do', debes adjuntar foto y comentario. Sin evidencia, el estado no se guarda.",
        evidence_required: "Requiere {{items}} para cambiar el estado.",
        evidence_photo: "foto",
        evidence_comment: "comentario",
        need_comment: "Agrega un comentario antes de cambiar el estado.",
        need_photo: "Sube al menos una foto antes de cambiar el estado.",
        locked_hint: "Sube foto y comentario para desbloquear",
        evidence_missing: "Falta {{items}} para poder marcar en curso / completo / no puedo.",
      },
      finding_assign: {
        edit_dialog_title: "TO DO",
        responsible_label: "Auditor responsable",
        select_placeholder: "Selecciona un auditor…",
        none_option: "Sin asignar",
        add_auditor: "+ Nuevo auditor",
        new_dialog_title: "Crear nuevo auditor",
        new_name: "Nombre completo",
        new_email: "Correo electrónico",
        new_password: "Contraseña temporal",
        new_role: "Rol",
        role_auditor: "Auditor",
        role_gerente: "Gerente",
        new_save: "Crear y asignar",
        notify_on_assign: "Recibirá una notificación in-app y verá el hallazgo en su dashboard.",
        created_assigned: "Auditor creado y asignado",
        assigned: "Asignado a {{name}}",
        unassigned: "Sin asignar",
      },
      my_findings: {
        title: "Mis hallazgos asignados",
        subtitle: "Pendientes que tienes que resolver tú.",
        empty: "Sin hallazgos asignados a ti. Buen trabajo 👌",
        view_all: "Ver todos",
        unread_notifications: "{{n}} sin leer",
        mark_read: "Marcar leídos",
        open: "Abrir",
      },
      dashboard_sections: {
        title: "Personaliza tu dashboard",
        subtitle: "Muestra u oculta cada sección. Tu elección se guarda en este navegador.",
        kpis: "Indicadores",
        activity: "Actividad semanal",
        trend: "Tendencia mensual",
        semaphore: "Distribución semáforo",
        ranking: "Ranking de tiendas",
        recent: "Hallazgos recientes",
        my_findings: "Mis hallazgos",
        share: "Visualizaciones",
        all_visible: "Todas visibles",
        hidden: "Oculto",
        show_all: "Mostrar todas",
        hide_all: "Ocultar todas",
        reset: "Restablecer",
      },
      activity_chart: {
        overline: "Actividad semanal",
        title: "Actividad de TO DOs",
        subtitle: "Abiertos vs. resueltos por día.",
        opened: "Abiertos",
        in_progress: "En curso",
        completed: "Completados",
        cannot_do: "No pude",
      },
      public_finding: {
        title: "Hallazgo a corregir",
        subtitle_role: "Eres el responsable de este hallazgo. Actualiza el estado, agrega notas y sube fotos cuando esté resuelto.",
        store: "Tienda",
        category: "Categoría",
        severity: "Severidad",
        description: "Descripción",
        notes: "Notas / observaciones",
        notes_placeholder: "Describe lo que hiciste, cuándo y cualquier observación…",
        upload_photo: "Subir foto de evidencia",
        add_more_photo: "Agregar otra foto",
        save_notes: "Guardar comentario",
        photos: "Fotos de evidencia",
        no_photos: "Aún no hay fotos.",
        author_label: "Tu nombre (opcional)",
        author_placeholder: "Para registrar quién actualizó",
        save: "Guardar cambios",
        saved: "Cambios guardados",
        save_error: "No se pudo guardar",
        upload_error: "No se pudo subir la foto",
        last_updated: "Última actualización: {{when}}",
        last_updated_by: "Actualizado por: {{by}}",
        translate_desc_en: "Traducir descripción a EN",
        translate_desc_es: "Traducir descripción a ES",
        translate_note_en: "Traducir nota a EN",
        translate_note_es: "Traducir nota a ES",
        translated: "Traducido",
        translate_error: "No se pudo traducir",
        show_original: "Ver original",
      },
      public: {
        title: "Reporte público",
        readonly: "Solo lectura",
        unavailable: "Enlace no disponible",
        expired: "Enlace expirado",
        expired_hint: "Pídele al auditor un nuevo enlace actualizado.",
        go_home: "Ir al inicio",
        signatures: "Firmas",
        signed_at: "Firmado: {{when}}",
      },
      share_app_msg: "Accede a la app de Auditoría Operativa de Mueblería Canales",
      share_app_copied: "Enlace de la app copiado",
    },
  },
  en: {
    translation: {
      brand: "Mueblería Canales",
      app_name: "Operations Audit",
      cookies: {
        title: "This site uses cookies",
        text: "We only use essential cookies for secure session authentication and to remember your language preference. We do not use advertising or third-party tracking cookies.",
        accept: "Accept",
        essential: "Essential only",
        more: "Privacy Policy",
        manage: "Cookie preferences",
        current_choice: "Current choice: {{choice}}",
        choice_all: "All",
        choice_essential: "Essential only",
        choice_none: "Not set",
        change: "Change",
        reset_confirm: "Your preferences will be reset. Next time you load any page the banner will appear again.",
      },
      common: {
        login: "Sign in",
        logout: "Sign out",
        email: "Email",
        password: "Password",
        name: "Name",
        role: "Role",
        enter: "Enter",
        cancel: "Cancel",
        save: "Save",
        delete: "Delete",
        edit: "Edit",
        close: "Close",
        back: "Back",
        search: "Search",
        loading: "Loading…",
        empty: "No data",
        actions: "Actions",
        yes: "Yes",
        no: "No",
        all: "All",
        download: "Download",
        share: "Share",
        share_app: "Share app",
        view_all_stores: "View all stores",
        my_store: "My store",
        change_password: "Change password",
        new_password: "New password",
        store: "Store",
        district: "District / Region",
        city: "City",
        score: "Score",
        status: "Status",
        category: "Category",
        question: "Question",
        weight: "Weight",
        criticality: "Criticality",
        assigned_store: "Assigned store",
        none: "None",
        copy: "Copy",
        copied: "Copied",
        clear: "Clear",
        active: "Active",
        inactive: "Inactive",
        next: "Next",
        previous: "Previous",
        uploading: "Uploading…",
        processing: "Processing…",
        upload_error: "File upload failed",
        take_photo: "Take photo",
        attach_file: "Attach file",
        upload_excel: "Upload Excel",
        files_uploaded: "{{n}} file(s) uploaded",
        file_too_big: "{{name}} is over 5MB — upload may be slow.",
        file_empty: "The file has no rows",
        file_read_error: "Could not read file: {{reason}}",
        loading_audit: "Loading audit…",
        loading_dashboard: "Loading dashboard…",
        loading_summary: "Loading summary…",
      },
      roles: {
        admin: "Administrator",
        auditor: "Auditor",
        gerente: "Manager",
        direccion: "Director",
      },
      nav: {
        dashboard: "Dashboard",
        assignments: "Tasks",
        stores: "Stores",
        checklist: "Checklist",
        audits: "Audits",
        findings: "Findings",
        followup: "Follow-up",
        games: "Game",
        flow: "Flow",
        users: "Users",
        permissions: "Permissions",
      },
      permissions: {
        title: "Role permissions",
        subtitle: "Configure what each role can see and do. Administrator permissions cannot be changed (always full access).",
        permission: "Permission",
        immutable: "Locked",
        save_per_role_hint: "Save changes per role →",
        saved: "{{role}} permissions saved",
        reset: "Reset changes",
        role_admin: "Administrator",
        role_auditor: "Auditor",
        role_gerente: "Manager",
        role_direccion: "Direction",
        k_view_dashboard: "View Dashboard",
        d_view_dashboard: "Access the main page with KPIs and charts.",
        k_view_stores: "View Stores",
        d_view_stores: "List and store catalog.",
        k_edit_stores: "Edit Stores",
        d_edit_stores: "Create, edit, or delete stores.",
        k_view_checklist: "View Checklist",
        d_view_checklist: "See configured questions and templates.",
        k_edit_checklist: "Edit Checklist",
        d_edit_checklist: "Create/edit questions, templates, and categories.",
        k_view_audits: "View Audits",
        d_view_audits: "Audit history and reports.",
        k_start_audit: "Start Audit",
        d_start_audit: "Begin an audit from the stores page.",
        k_edit_audit: "Edit Audit",
        d_edit_audit: "Continue or reopen audits to correct responses.",
        k_delete_audit: "Delete Audit",
        d_delete_audit: "Delete individual reports (high risk).",
        k_view_findings: "View Findings",
        d_view_findings: "List of pending and completed findings.",
        k_edit_findings: "Edit Findings",
        d_edit_findings: "Assign owner, change status, and add notes.",
        k_share_finding: "Share Finding",
        d_share_finding: "Generate a public link for the assignee to update.",
        k_delete_finding: "Delete Finding",
        d_delete_finding: "Delete individual findings.",
        k_send_sms: "Send SMS",
        d_send_sms: "Send SMS to assignees from a finding (Twilio).",
        k_view_followup: "View Follow-up",
        d_view_followup: "Board with deadlines and overdues.",
        k_view_users: "View Users",
        d_view_users: "List of users and their roles.",
        k_manage_users: "Manage Users",
        d_manage_users: "Create, edit, disable users.",
        k_view_my_findings: "View My findings",
        d_view_my_findings: "Dashboard widget of findings assigned to the user.",
        k_manage_permissions: "Manage Permissions",
        d_manage_permissions: "Access this page and edit the role matrix.",
        k_view_assignments: "View Tasks & Audits",
        d_view_assignments: "See assigned tasks and audits.",
        k_manage_assignments: "Manage Tasks & Audits",
        d_manage_assignments: "Create, assign, reassign, change dates and cancel tasks.",
      },
      logic: {
        title: "Conditional logic",
        add_logic: "Add logic",
        flagged_title: "Flagged responses (issue)",
        flagged_hint: "Which answers count as a flag/finding for this question.",
        use_default: "Use default",
        if_answer: "If answer is",
        then: "Then",
        add_rule: "Add rule",
        trg_instruction: "Show instruction / message to auditor",
        trg_instruction_ph: "e.g. It's important the manager receives the inspection results.",
        trg_require_photo: "Require photo",
        trg_require_comment: "Require comment",
        trg_followup: "Follow-up question",
        trg_followup_ph: "e.g. What is the root cause?",
        trg_followup_required: "Required",
        trg_create_action: "Create corrective action (TO DO)",
        trg_action_severity: "Severity",
        trg_action_assignee: "Assignee",
        trg_action_assignee_ph: "e.g. Store manager",
        trg_action_due: "Due in (days)",
        trg_notify: "Notify (bell)",
        trg_block_close: "Block close until resolved",
        save: "Save logic",
        saved: "Logic saved",
        error: "Could not save logic",
        tpl_insert: "Insert template",
        tpl_save: "Save as template",
        tpl_saved: "Template saved",
        tpl_empty: "Write an instruction first",
        tpl_none: "No templates yet",
        trg_manual: "Manual / reference file",
        trg_manual_hint: "Shown below the question when the rule applies.",
        trg_manual_upload: "Attach manual",
        preview_title: "Preview",
        preview_when: "When answering {{answers}}, the auditor sees:",
        no_answer_options: "This response type (text, number, media, signature) doesn't use per-answer rules.",
      },
      games: {
        module: "Training / Game",
        subtitle: "A 'Family Feud' style game to train your team on the Canales standards.",
        new_game: "New game",
        edit_game: "Edit game",
        tab_games: "Games",
        tab_history: "History",
        questions: "questions",
        inactive: "Inactive",
        active: "Active (visible to play)",
        play: "Play",
        copy_link: "Copy link to project",
        link_copied: "Link copied",
        confirm_delete: "Delete the game \"{{title}}\"?",
        deleted: "Game deleted",
        no_matches: "No matches played yet.",
        col_game: "Game",
        col_result: "Result",
        col_winner: "Winner",
        col_host: "Host",
        col_date: "Date",
        empty_title: "Create your first game",
        empty_desc: "Build a Family Feud style board with questions and answers, or generate them with AI from your checklist.",
        saved: "Game saved",
        save_error: "Could not save the game",
        title_required: "Enter a title",
        need_one_question: "Add at least one question with answers",
        editor_hint: "Each question holds up to 8 answers with points (summing ~100).",
        game_title: "Title",
        game_description: "Description",
        ai_title: "Generate with AI",
        ai_desc: "Create questions and answers from the Canales manual/checklist.",
        ai_count: "Count",
        ai_topic: "Topic (optional)",
        ai_topic_ph: "e.g. image, display, MOTO",
        ai_generate: "Generate",
        ai_empty: "AI returned no questions, try again",
        ai_added: "Added {{count}} questions",
        ai_error: "Could not generate with AI",
        question_ph: "Write the survey question…",
        answer_ph: "Answer",
        points: "Pts",
        add_answer: "Add answer",
        add_question: "Add question",
        not_found: "Game not found",
        not_available: "This game is not available",
        brand_tagline: "Canales Training",
        team_a: "Team A",
        team_b: "Team B",
        start_game: "Start!",
        tie: "Tie",
        winner: "Winner",
        save_match: "Save result",
        match_saved: "Result saved",
        play_again: "Play again",
        back_to_games: "Back to games",
        question: "Question",
        round_points: "Points at stake",
        strike: "Strike",
        reset_round: "Reset round",
        next: "Next",
        finish: "Finish",
        show_key: "Show answers (host)",
        hide_key: "Hide answers",
        decoys_title: "Wrong answers (decoys)",
        decoys_hint: "These don't appear on the board. Picking one loses the turn.",
        decoy_ph: "Wrong but believable answer",
        add_decoy: "Add decoy",
        live: {
          title: "Live match",
          play_live: "Live",
          setup_hint: "Two stores play from their own screen. You run it as the host.",
          store_a: "Store A",
          store_b: "Store B",
          turn_mode: "Turn mode",
          mode_strikes: "Classic (3 strikes + steal)",
          mode_strikes_desc: "The team banks points; after 3 strikes the other team can steal the round.",
          mode_instant: "Fast (1 miss = turn passes)",
          mode_instant_desc: "Each correct scores; on the first miss the turn passes to the other team.",
          create_room: "Create room",
          create_error: "Could not create the room",
          join_at: "Join at",
          room_code: "Room code",
          copy_join_link: "Copy room link",
          start_match: "Start match",
          waiting_teams: "You can start once both stores are connected.",
          close_room: "Close room",
          connected: "✓ Connected",
          waiting: "Waiting…",
          join_title: "Join the match",
          join_hint: "Enter the code shown by the host.",
          enter: "Enter",
          enter_code: "Enter the room code",
          room_not_found: "Room not found",
          try_another: "Try another code",
          pick_your_store: "Pick your store",
          tap_to_join: "Tap to join",
          slot_taken_hint: "Already connected (you can reconnect)",
          you_are: "You are",
          waiting_host: "Waiting for the host to start…",
          choose_answer: "Choose an answer",
          your_turn: "Your turn! Pick an answer",
          other_turn: "{{team}}'s turn",
          turn_of: "{{team}}'s turn",
          round_over: "Round over — wait for the host",
          steal_banner: "Steal! {{team}} gets one chance",
          match_saved: "Result saved to history",
          new_match: "New match",
          host_key_hint: "Green dots = correct · red = decoys (only you see them).",
          pick_error: "Could not register your answer",
        },
      },
      response_types: {
        canales: "Canales (Yes/No/In progress/Warning/Fine)",
        good_fair_poor: "Good / Fair / Poor",
        pass_fail: "Pass / Fail",
        compliant: "Compliant / Non-compliant",
        safe_risk: "Safe / At risk",
        text: "Text",
        number: "Number",
        media: "Photo / Media",
        signature: "Signature",
      },
      assignments: {
        overline: "Tasks & Audits",
        title: "Assigned Tasks & Audits",
        subtitle: "Assign audits and tasks to your team, track progress and review results.",
        my_tasks_title: "My tasks",
        my_tasks_subtitle: "Tasks and audits assigned to you.",
        new_assignment: "New task",
        tab_list: "List",
        tab_schedule: "Weekly schedule",
        all: "All",
        filter_auditor: "Auditor",
        filter_store: "Store",
        filter_status: "Status",
        filter_category: "Category",
        filter_type: "Type",
        clear_filters: "Clear filters",
        search_ph: "Search task, store or auditor…",
        status_pending: "Pending",
        status_in_progress: "In progress",
        status_completed: "Completed",
        status_overdue: "Overdue",
        status_cancelled: "Cancelled",
        priority: "Priority",
        priority_low: "Low",
        priority_medium: "Medium",
        priority_high: "High",
        type: "Type",
        type_store_audit: "Store audit",
        type_task_checklist: "Task checklist",
        auditor: "Assigned auditor",
        store: "Store",
        categories: "Categories",
        assigned_date: "Assigned date",
        due_date: "Due date",
        instructions: "Instructions",
        comment: "Comments",
        attachments: "Attachments",
        task_title: "Task title",
        due: "Due",
        assigned: "Assigned",
        no_due: "No due date",
        start_task: "Start task",
        open_audit: "Open audit",
        continue_audit: "Continue audit",
        view: "View",
        view_detail: "View detail",
        complete_task: "Complete task",
        reassign: "Reassign",
        cancel_task: "Cancel task",
        delete_task: "Delete",
        back: "Back to tasks",
        details: "Details",
        history_title: "Task history",
        comments_title: "Conversation",
        add_comment: "Send",
        comment_ph: "Write a comment…",
        no_comments: "No comments yet.",
        attach_photo: "Attach photo",
        checklist_title: "Checklist",
        item_complete: "Complete",
        item_incomplete: "Incomplete",
        item_na: "N/A",
        item_pending: "Pending",
        item_comment_ph: "Comment…",
        add_photo: "Add photo",
        add_another_photo: "Add another photo",
        progress: "Progress",
        reassign_title: "Reassign task",
        reassign_to: "New auditor",
        reassign_note: "Note (optional)",
        confirm_reassign: "Reassign",
        reassigned_from: "Reassigned from {{from}} to {{to}}",
        create_title: "New task or audit",
        create_subtitle: "Assign a store audit or a task checklist to an auditor.",
        choose_type: "Assignment type",
        choose_type_audit: "Store audit",
        choose_type_audit_desc: "Reuse the audit engine with the chosen categories.",
        choose_type_task: "Task checklist",
        choose_type_task_desc: "Internal task list (tools, knowledge, weekly).",
        choose_categories: "Categories to audit",
        choose_categories_hint: "Select the templates the auditor must complete.",
        checklist_items: "Checklist items",
        add_item: "Add item",
        item_ph: "e.g. Check tools",
        remove: "Remove",
        attach_files: "Attach files or photos",
        save: "Create task",
        empty_title: "No tasks yet",
        empty_hint: "Create the first task to assign audits to your team.",
        empty_auditor: "You have no assigned tasks.",
        no_results: "No tasks match these filters.",
        schedule_title: "Auditor weekly schedule",
        week: "Week",
        this_week: "This week",
        next_week: "Next week",
        prev_week: "Previous week",
        col_auditor: "Auditor",
        col_store: "Store",
        col_assignment: "Assignment",
        col_status: "Status",
        no_schedule: "No assignments this week.",
        cancel_confirm: "Cancel this task? The history is kept.",
        delete_confirm: "Delete this task permanently?",
        result_score: "Result",
        time_spent: "Time spent",
        completed_by: "Completed by",
        created_by: "Created by",
        not_started: "Not started",
        instructions_from: "Instructions from admin",
        no_instructions: "No instructions.",
        no_attachments: "No attachments.",
        toast_created: "Task created and assigned",
        toast_updated: "Task updated",
        toast_reassigned: "Task reassigned",
        toast_started: "Task started",
        toast_completed: "Task completed",
        toast_cancelled: "Task cancelled",
        toast_deleted: "Task deleted",
        toast_comment: "Comment sent",
        toast_item_saved: "Item updated",
        toast_error: "Something went wrong",
        complete_blocked: "Resolve every checklist item before completing.",
        need_auditor: "Select an auditor.",
        need_store: "Select a store.",
        need_category: "Select at least one category.",
        need_items: "Add at least one checklist item.",
        store_history: "Audit & task history",
        minutes_short: "min",
        drag_hint: "Drag an assignment onto another auditor to reassign it.",
      },
      login: {
        title: "Sign in",
        subtitle: "Operational access · Mueblería Canales",
        hero_title: "Operational auditing,",
        hero_title_2: "right on the sales floor.",
        hero_desc: "Capture evidence, score with weights, log findings and signed PDF reports — all in one guided flow for your stores.",
        forgot: "Forgot your password? Ask the admin to reset it.",
        bad_credentials: "Invalid email or password",
        signing_in: "Signing in…",
        badge: "v2.1 · Operational mode",
        bullet_1: "Guided audits, photos, findings, and signatures in a single flow.",
        bullet_2: "Signed PDF reports with QR to share with Leadership.",
        bullet_3: "Works on phone, iPad, and computer.",
      },
      dashboard: {
        title: "Executive dashboard",
        greeting: "Hi, {{name}}.",
        subtitle_global: "Operational view of your stores.",
        subtitle_mine: "Indicators of your assigned store.",
        no_audits: "No audits yet. Start your first one from the store catalog.",
        hero_paragraph: "Key indicators, traffic-light scoring, and ranking in real time.",
        kpi_audits: "Audits",
        kpi_closed: "Closed",
        kpi_in_progress: "In progress",
        kpi_avg: "Avg. score",
        kpi_stores: "Stores",
        kpi_findings: "Findings",
        kpi_views: "Report views",
        start_audit: "Start audit",
        view_history: "View history",
        recent_findings_subtitle: "Actions that need attention",
        trend: "Monthly trend",
        trend_subtitle: "Average score by month",
        semaphore: "Traffic-light distribution",
        semaphore_subtitle: "Stores by performance",
        ranking: "Store ranking",
        ranking_subtitle: "Top closed audits",
        recent_findings: "Recent findings",
        share_activity_title: "Who is reading the reports?",
        share_activity_subtitle: "Public links you've shared and how many times they've been opened.",
        share_empty: "No shared reports yet. Open any closed audit and press Share.",
      },
      stores: {
        title: "Store catalog",
        hero_title: "Pick the store you'll audit.",
        hero_desc: "Import your Excel, search by name or city, and start the guided flow with one tap.",
        empty_title: "No stores to show",
        empty_admin: "Import your catalog from Excel or add a store manually to get started.",
        empty_other: "Ask the Admin to load the store catalog.",
        new_store: "New store",
        template: "Template",
        import: "Import",
        import_excel: "Import Excel",
        audit: "Audit",
        readonly: "Read only",
        no_audited: "Not audited",
        search_placeholder: "Search store, district or city…",
        filter_all: "All",
        filter_green: "Green",
        filter_yellow: "Yellow",
        filter_red: "Red",
        filter_pending: "Not audited",
        confirm_delete: 'Delete "{{name}}"?',
        added: "Store added",
        required: "Store name is required",
        imported: "{{n}} stores imported",
        headers_error: "Expected headers: Tienda, Distrito, Ciudad",
        audit_started: "Audit started at {{store}}",
        audit_start_error: "Could not start the audit",
        role_error: "Only Auditor or Admin can start audits",
        edit_score: "Edit score",
        edit_score_hint: "Manually adjust the score and traffic-light status.",
        score_label: "Score (0–100)",
        semaphore_label: "Traffic light",
        semaphore_auto: "Automatic",
        score_invalid: "Enter a valid score between 0 and 100",
        score_updated: "Score updated",
        score_error: "Could not update the score",
      },
      audit: {
        in_field: "Field audit",
        loading: "Loading audit…",
        progress: "Progress: {{answered}} of {{total}} questions",
        close: "Close audit",
        closed: "Closed",
        no_checklist: "No checklist loaded",
        no_checklist_desc: "Import an Excel file with columns: Template, Category, Question, Weight, Criticality",
        upload_checklist: "Upload checklist",
        cumple: "Yes",
        no_cumple: "No",
        na: "N/A",
        yes: "Yes",
        no: "No",
        en_proceso: "In progress",
        warning: "Warning",
        multa: "Fine",
        good: "Good",
        fair: "Fair",
        poor: "Poor",
        pass: "Pass",
        fail: "Fail",
        compliant: "Compliant",
        non_compliant: "Non-compliant",
        safe: "Safe",
        at_risk: "At risk",
        text_answer_ph: "Type the answer…",
        number_answer_ph: "Enter a number…",
        media_answer: "Upload photo",
        signature_save: "Save signature",
        signature_saved: "Signature saved",
        signature_empty: "Draw the signature first",
        view_signature: "View signature",
        manual: "Reference manual",
        view_manual: "View manual",
        comment: "Comment",
        photo: "Photo",
        comment_required: "Describe the issue (required)…",
        comment_optional: "Optional comment…",
        photo_required: "Evidence photo (required)",
        photo_optional: "Optional photo",
        auditor_label: "Auditor",
        weight_label: "Weight",
        criticality_label: "Criticality",
        saving: "Saving…",
        comment_saved: "Comment saved",
        save_error: "Could not save the response",
        audit_closed: "Audit closed",
        confirm_close: "{{n}} questions remain unanswered. Close anyway?",
        checklist_headers_error: "Expected headers: Template, Category, Question, Weight, Criticality",
        checklist_updated: "Checklist updated: {{n}} questions",
        template_label: "Template",
        no_questions_in_template: "This template has no questions yet. Add or import questions in the Checklist section.",
        comment_required_error: "Comment is required for this response.",
        followup_required_error: "You must answer this follow-up question.",
        photo_required_error: "Photo is required for this response.",
        evidence_banner_title: "{{n}} question(s) with incomplete evidence.",
        evidence_banner_desc: "Before closing the audit, complete the required evidence (comment, photo and/or follow-up answer) on flagged responses.",
        evidence_jump: "Jump to the first pending one",
        evidence_missing_close: "Missing evidence (photo + note) in {{n}} question(s). Start with: \"{{q}}…\"",
        next_unanswered: "Next unanswered",
        go_top: "Back to top",
      },
      cover: {
        title: "Start audit",
        subtitle: "Fill in the cover page before starting.",
        auditor_name: "Auditor name",
        manager_name: "Manager on duty",
        manager_placeholder: "e.g. José López",
        address: "Address",
        address_placeholder: "Street, number, city",
        date: "Audit date",
        audit_number: "Audit number",
        audit_number_hint: "It will be auto-generated, you can edit it.",
        choose_template: "Choose the template to audit",
        choose_template_multi: "Choose one or more templates to audit",
        template_subtitle: "Each template has its own set of questions. You can pick several and run them in a single audit.",
        new_template: "New template",
        new_template_dialog: "Create template",
        template_name: "Template name",
        template_description: "Description (optional)",
        questions_count: "{{n}} questions",
        no_questions: "No questions",
        start: "Start audit",
        start_audit_with: "Start audit · {{template}}",
        start_audit_multi: "Start audit · {{n}} templates",
        manager_required: "Enter the manager-on-duty name",
        template_required: "Select a template",
        template_created: "Template created",
        template_exists: "A template with that name already exists",
        delete_template_confirm: "Delete template \"{{name}}\"?",
      },
      multa: {
        title: "Apply fine",
        subtitle: "Capture the amount, justification, and who to notify.",
        amount: "Fine amount",
        amount_placeholder: "0.00",
        currency: "USD",
        reason_label: "Justification",
        reason_placeholder: "Explain why the fine is being applied…",
        ai_generate: "Generate with AI",
        ai_regenerate: "Regenerate",
        ai_generating: "Generating with Claude…",
        ai_error: "AI generation failed",
        recipients_label: "Notify",
        no_recipients_available: "No auditors available to notify.",
        no_recipients_selected: "Select at least one recipient",
        save_and_notify: "Save and notify",
        saved_only: "Save without notifying",
        notified: "Notification sent to {{n}} user(s)",
        observation_label: "Additional observation (optional)",
        observation_placeholder: "Extra context for the AI…",
      },
      notifications: {
        title: "Notifications",
        empty: "You have no notifications.",
        from: "From",
        store: "Store",
        amount: "Amount",
        mark_read: "Mark as read",
        mark_all_read: "Mark all as read",
        unread: "Unread",
        unread_n: "{{n}} unread",
        by: "by",
        showing_n: "{{n}} shown",
        view_all: "View all",
        kind_started: "TO DO in progress",
        kind_done: "TO DO completed",
        kind_cannot_do: "Cannot do",
        kind_reopened: "Reopened to TO DO",
        kind_note: "Comment updated",
        kind_photo: "Photo added",
        kind_assignment_assigned: "Task assigned",
        kind_assignment_reassigned: "Task reassigned",
        kind_assignment_started: "Task started",
        kind_assignment_completed: "Task completed",
        kind_assignment_comment: "Task comment",
        kind_audit_flag: "Flagged audit response",
        page_title: "Activity history",
        page_subtitle: "Every move on a TO DO link is logged here.",
        total: "Total",
        showing: "Showing",
        search_ph: "Search by description, author…",
        filter_all: "All types",
        filter_store_ph: "Filter by store…",
        only_unread: "Unread only",
        empty_filtered: "No notifications match your filters.",
        pagination: "Showing {{from}}–{{to}} of {{total}}",
        view_photo: "View photo",
        export_csv: "Export CSV",
        exported: "CSV downloaded",
      },
      audits_list: {
        title: "Audit history",
        subtitle: "Continue an in-progress audit or check the summary of closed ones.",
        in_progress: "In progress",
        empty: "No audits yet. Start one from the store catalog.",
        view_summary: "Summary",
        continue: "Continue",
        auditor: "Auditor",
        start: "Started",
        delete: "Delete",
        confirm_delete: "Delete the audit for \"{{store}}\" started on {{date}}?\n\nResponses, findings and notifications linked to it will be removed. This cannot be undone.",
        deleted: "Audit deleted",
        delete_error: "Could not delete the audit",
        edit_report: "Edit",
        reopen: "Reopen audit",
        confirm_reopen: "Reopen the audit for \"{{store}}\". It will switch back to edit mode — you can change responses and add evidence.",
        reopened: "Audit reopened",
      },
      flow: {
        title: "Operational Flow",
        hero_title: "10 guided steps.",
        hero_desc: "Follow the flow in order to ensure a complete audit with evidence and signatures.",
        continue_active: "Continue active audit",
        go_step: "Go to step",
        steps: {
          s1_title: "Pick a store",
          s1_desc: "Select the target store in the catalog. It will be marked as the active store.",
          s2_title: "Load checklist",
          s2_desc: "Import the checklist (category, question, weight, criticality) or use the loaded one.",
          s3_title: "Start audit",
          s3_desc: "From the selected store, press 'Audit' to open the field flow.",
          s4_title: "Field audit",
          s4_desc: "Answer Pass / Fail / N/A. Capture photos and comments.",
          s5_title: "Generate findings",
          s5_desc: "Each Fail creates a finding automatically, ready to assign an owner.",
          s6_title: "Compute score",
          s6_desc: "The weighted score is computed automatically with the traffic-light status.",
          s7_title: "Assign owners",
          s7_desc: "In Findings define owner, due date, and severity.",
          s8_title: "Manager follow-up",
          s8_desc: "Manager updates the status of corrective actions.",
          s9_title: "Summary and signatures",
          s9_desc: "Capture name and signature of Manager and Regional. Generate the AI summary.",
          s10_title: "Generate PDF",
          s10_desc: "Download the signed executive PDF report.",
        },
      },
      checklist_page: {
        title: "Checklist questions",
        subtitle: "Define the audit questions with their category, weighted score, and criticality.",
        new_question: "New question",
        edit_question: "Edit question",
        response_type: "Response type",
        response_type_hint: "Defines which options or capture field this question uses.",
        edit_template: "Edit template",
        drag_to_reorder: "Drag to reorder",
        template_color: "Color",
        template_color_custom: "Custom",
        delete_template: "Delete template",
        template_deleted: "Template deleted",
        confirm_delete_template: "Delete the template \"{{name}}\"?",
        confirm_delete_template_force: "\"{{name}}\" is in use: {{checklist}} checklist question(s) and {{audits}} audit(s). If you continue, questions keep the name as plain text but won't show under the filter. Continue?",
        empty: "No questions. Import an Excel or add manually.",
        confirm_delete: "Delete question?",
        added: "Question added",
        updated: "Question updated",
        required: "Category and question are required",
        category_placeholder: "Living, Bedroom, Financing…",
        translate: "Translate to {{lang}}",
        translate_tooltip: "Auto-generate the active-language version with AI. Translations are cached and reused.",
        translating: "Translating…",
        translated: "{{n}} translated (of {{total}})",
        translate_error: "Translation failed. Please retry or check the server logs.",
        translate_error_http: "Translation failed (HTTP {{code}}). Please retry.",
        pdf_photo: "PDF / Photo",
        extracting: "Extracting…",
        pdf_tooltip: "Extract questions with AI from PDF or photo",
      },
      summary: {
        title: "Final Summary",
        ai_title: "AI executive summary",
        ai_subtitle: "Automatic narrative",
        ai_placeholder: "Generate an automatic summary with Claude Sonnet 4.5 or write your own…",
        generate_ai: "Generate summary",
        regenerate_ai: "Regenerate",
        gerente_name: "Store manager",
        regional_name: "Regional / Auditor",
        save_signatures: "Save signatures",
        gerente_signature: "Store manager signature",
        regional_signature: "Regional / Auditor signature",
        sign_here: "Sign here",
        download_pdf: "Download PDF",
        category_subtotals: "Category subtotals",
        attachments: "Attachments",
        share_report: "Share report",
        share_desc: "Anyone with this link will be able to view the read-only report — no login required.",
        no_link: "No link yet.\nPick an expiration below and generate.",
        expiration: "Link expiration",
        no_expiry: "No expiration",
        days_7: "7 days",
        days_30: "30 days",
        days_90: "90 days",
        generate_link: "Generate link",
        update_expiry: "Update expiration",
        revoke: "Revoke link",
        confirm_revoke: "Revoke the public link? Anyone holding it will no longer be able to view it.",
        link_revoked: "Link revoked",
        link_copied: "Link copied to clipboard",
        copy_failed: "Could not copy the link",
        views: "Views",
        last_viewed: "Last: {{when}}",
        not_viewed: "Not opened yet",
        expires_on: "Expires: {{date}}",
        in_n_days: "in {{n}} day(s)",
        tip_qr: "Tip: The QR is also printed on the last page of the PDF.",
        link_error: "Could not generate the link",
        ai_success: "AI summary generated",
        ai_error: "Could not generate the AI summary",
        signatures_saved: "Signatures and summary saved",
      },
      users: {
        title: "Users",
        subtitle: "Only the administrator can create, edit, or deactivate users.",
        new_user: "New user",
        edit_user: "Edit user",
        delete_confirm: "Delete {{name}}? This is permanent.",
        locked: "Locked",
        locked_until_tooltip: "Account locked from failed attempts until {{when}}",
        unlock: "Unlock",
        unlock_confirm: "Unlock the account for {{name}}?",
        unlocked: "{{name}}'s account unlocked",
        reset_password: "Reset password",
        reset_password_prompt: "Enter the new password for {{name}} (min 6 characters). The user can log in immediately.",
        password_too_short: "Password must be at least 6 characters",
        password_reset: "{{name}}'s password reset",
        leave_password_blank: "Leave blank to keep the password.",
        no_assignment: "No store assigned (global access)",
        phone_label: "Phone (with country code)",
        phone_placeholder: "+1 555 123 4567",
        phone_hint: "International format with +. Used to receive automatic SMS.",
        phone_col: "Phone",
        optin_title: "SMS Opt-In Requests",
        optin_hint: "People who signed up via the public /sms-optin form. Link them to a user to assign the phone number.",
        optin_link: "Link",
        optin_select_user: "Select user…",
        optin_linked_toast: "Phone linked to user",
        notifications_title: "Notification preferences (SMS)",
        notifications_hint: "Toggle which events trigger an informational SMS to this user.",
        notif_multa: "Fine applied in an audit",
        notif_to_do: "TO DO assigned to me",
        notif_no_followup_24h: "TO DO not followed up in 24h",
        notif_reports: "Report ready (audit closed)",
        notif_alerts: "General alerts",
        notif_todo_completed: "TO DO completed from public checklist",
        notif_assignment: "Task/audit assigned or reassigned to me",
        notif_task_update: "A task I created/manage was started, completed or commented",
        notif_overdue: "A TO DO I'm responsible for (or manage) passed its due date",
        bulk_selected: "{{count}} selected",
        bulk_unlock: "Unlock",
        bulk_reset: "Reset passwords",
        bulk_unlock_confirm: "Unlock {{count}} selected account(s)?",
        bulk_unlocked: "{{count}} account(s) unlocked",
        bulk_reset_confirm: "Generate temporary passwords for {{count}} user(s)? They'll have to change it on next login.",
        bulk_reset_done_title: "Temporary passwords generated",
        bulk_reset_done_desc: "These passwords are shown only once. Copy or download them now. Each user must choose their own password when they sign in.",
        temp_password: "Temporary password",
        download_csv: "Download CSV",
        select_all: "Select all",
        select_row: "Select {{name}}",
      },
      force_pw: {
        title: "Choose your new password",
        subtitle: "An administrator reset your access with a temporary password. For security, pick your own password before continuing.",
        new: "New password",
        confirm: "Confirm password",
        submit: "Update password",
        logout: "Sign out",
        too_short: "Password must be at least 6 characters",
        mismatch: "Passwords don't match",
        updated: "Password updated",
        error: "Could not update",
      },
      followup: {
        title: "Manager Follow-up",
        subtitle: "Update the status of each corrective action and add follow-up notes.",
        readonly_role: "(Read only for your role.)",
        all_stores: "All stores",
        responsible: "Responsible",
        commitment_date: "Commitment date",
        notes: "Notes",
        notes_placeholder: "Observation, next step, evidence…",
      },
      findings: {
        title: "Findings",
        subtitle: "Every Fail creates a finding. Assign owner, due date, and severity.",
        no_findings: "No findings.",
        edit_finding: "Edit finding",
        page_subtitle: "Detected non-compliance",
        search_placeholder: "Search by description, store, or category…",
        all_status: "All",
        col_store: "Store",
        col_category: "Category",
        col_description: "Description",
        col_responsible: "Owner",
        col_commitment: "Due date",
        col_severity: "Severity",
        col_status: "Status",
        col_action: "Action",
        save_changes: "Save changes",
        updated: "Finding updated",
        notes_label: "Follow-up notes",
        confirm_delete: "Delete this finding?",
        deleted: "Finding deleted",
        delete_error: "Could not delete the finding",
        view_table: "Table",
        view_group: "Group by store",
        group_total: "{{n}} findings",
        send_checklist: "Send checklist SMS",
        col_photos: "Photos",
        auto_refresh: "Auto-refresh 15s",
      },
      severity: {
        Alta: "High",
        Media: "Medium",
        Baja: "Low",
      },
      finding_status: {
        to_do: "To do",
        in_progress: "In progress",
        done: "Completed",
        cannot_do: "I can't do it",
        Pendiente: "To do",
        "En Proceso": "In progress",
        Cerrado: "Completed",
      },
      finding_share: {
        share_button: "Share finding",
        title: "Share with the assignee",
        subtitle: "Generate a public link so the assignee can update the status, add notes and upload photos — no login required.",
        generate: "Generate link",
        copy: "Copy link",
        copied: "Link copied",
        copy_failed: "Could not copy the link",
        revoke: "Revoke link",
        confirm_revoke: "Revoke the public link? Anyone holding it will no longer be able to view or update this finding.",
        revoked: "Link revoked",
        link_active: "Active link",
      },
      sms: {
        title: "Send SMS",
        subtitle: "Notify one or more assignees about this task by text message.",
        recipients: "Recipient phones",
        add_phone: "Add another number",
        format_hint: "Include the country code (e.g. +1)",
        placeholder: "+1 5551234567",
        message: "Message",
        message_placeholder: "Type the message to send…",
        chars_left: "chars left",
        send: "Send SMS ({{n}})",
        sending: "Sending…",
        sent_n: "{{n}} SMS sent",
        failed_n: "{{n}} failed",
        send_error: "Could not send (check Twilio or retry).",
        summary: "{{ok}} sent · {{fail}} failed",
        not_configured: "SMS is not configured on this server.",
        button_label: "SMS",
        choose_team: "Choose from team",
        team_count: "{{n}} with phone",
        team_search: "Search by name, email, or role…",
        team_empty: "No user has a phone on file. Capture it in Users to pick them here.",
        max_recipients: "Maximum {{n}} recipients.",
      },
      group_sms: {
        title: "Share checklist",
        subtitle: "A single SMS with all pending items grouped by store.",
        subtitle_multichannel: "Share the pending list via SMS, email, Teams or copy the link. All open the same interactive page.",
        total_findings: "{{n}} findings in total",
        include_statuses: "Statuses to include",
        findings_included: "TO DOs included",
        empty: "No findings for this store.",
        no_selected: "Select at least one TO DO.",
        share_link_label: "Link with TO DO preview image",
        create_link: "Create link with TO DO preview",
        creating_link: "Generating link…",
        link_ready: "Link ready with TO DO image",
        link_ready_short: "Ready",
        share_link_hint: "This link shows a 'TO DO' preview image when shared via SMS/WhatsApp. Generate the link to include it in the message.",
        share_link_hint_short: "Generated automatically on send. Shows the 'TO DO' image when the recipient receives the link.",
        send: "Send checklist ({{n}})",
        send_sms: "Send SMS ({{n}})",
        send_email: "Open email ({{n}})",
        send_teams: "Share to Teams",
        send_whatsapp: "Open WhatsApp ({{n}})",
        whatsapp_label: "WhatsApp numbers",
        whatsapp_default: "Open the interactive checklist:",
        whatsapp_opened: "Opening WhatsApp",
        whatsapp_multi_hint: "WhatsApp opens one chat per recipient. Send each one manually.",
        whatsapp_empty_hint: "With no recipients WhatsApp opens with the message ready — you pick who to send it to.",
        per_findings: "{{n}} findings",
        reset_message: "Restore auto-generated message",
        channel_label: "Channel",
        channel_sms: "SMS",
        channel_email: "Email",
        channel_teams: "Teams",
        channel_whatsapp: "WhatsApp",
        channel_copy: "Copy",
        pick_contacts: "Choose contacts",
        contacts_available: "{{n}} available",
        no_emails: "No user has an email on file for this channel.",
        emails_label: "Recipient emails",
        add_email: "Add another email",
        teams_placeholder: "user@company.com",
        optional_note: "Optional note (prepended to the link)",
        optional_note_placeholder: "e.g. Priority this week, please review before Friday…",
        no_prefill_hint: "Only the link is sent — the recipient opens the interactive list and updates each TO DO (photo + comment required).",
        email_subject: "TO DO checklist",
        email_default_body: "Open the interactive checklist:",
        teams_default: "Open the interactive checklist:",
        email_opened: "Email client opened",
        teams_opened: "Sharing in Teams",
        sms_default: "TO DO checklist",
        copy_link: "Copy link",
        copied: "Link copied to clipboard",
        copy_failed: "Could not copy",
      },
      public_checklist: {
        overline: "Canales · TO DO Checklist",
        title: "Store pending items",
        count: "{{n}} pending",
        by: "Sent by",
        empty: "This checklist has no pending items.",
        responsible: "Owner",
        no_responsible: "Unassigned",
        open_item: "Open to update",
        load_error: "Could not load the checklist.",
        not_found: "Checklist not available",
        progress: "Progress",
        rule_hint: "When marking any status other than 'To do', you must attach a photo and a comment. Without evidence, the status won't save.",
        evidence_required: "Requires {{items}} to change status.",
        evidence_photo: "photo",
        evidence_comment: "comment",
        need_comment: "Add a comment before changing the status.",
        need_photo: "Upload at least one photo before changing the status.",
        locked_hint: "Upload photo and comment to unlock",
        evidence_missing: "Missing {{items}} to mark in progress / done / cannot do.",
      },
      permissions_extra: {
        k_send_sms: "Send SMS",
        d_send_sms: "Send SMS to assignees from a finding (Twilio).",
      },
      finding_assign: {
        edit_dialog_title: "TO DO",
        responsible_label: "Responsible auditor",
        select_placeholder: "Select an auditor…",
        none_option: "Unassigned",
        add_auditor: "+ New auditor",
        new_dialog_title: "Create new auditor",
        new_name: "Full name",
        new_email: "Email address",
        new_password: "Temporary password",
        new_role: "Role",
        role_auditor: "Auditor",
        role_gerente: "Manager",
        new_save: "Create and assign",
        notify_on_assign: "They will get an in-app notification and see the finding on their dashboard.",
        created_assigned: "Auditor created and assigned",
        assigned: "Assigned to {{name}}",
        unassigned: "Unassigned",
      },
      my_findings: {
        title: "My assigned findings",
        subtitle: "Pending items you need to resolve.",
        empty: "No findings assigned to you. Nice job 👌",
        view_all: "View all",
        unread_notifications: "{{n}} unread",
        mark_read: "Mark as read",
        open: "Open",
      },
      dashboard_sections: {
        title: "Customize your dashboard",
        subtitle: "Show or hide each section. Your choice is saved on this browser.",
        kpis: "KPIs",
        activity: "Weekly activity",
        trend: "Monthly trend",
        semaphore: "Traffic-light spread",
        ranking: "Store ranking",
        recent: "Recent findings",
        my_findings: "My findings",
        share: "Report views",
        all_visible: "All visible",
        hidden: "Hidden",
        show_all: "Show all",
        hide_all: "Hide all",
        reset: "Reset",
      },
      activity_chart: {
        overline: "Weekly activity",
        title: "TO DO activity",
        subtitle: "Opened vs. resolved per day.",
        opened: "Opened",
        in_progress: "Started",
        completed: "Completed",
        cannot_do: "Cannot do",
      },
      public_finding: {
        title: "Finding to fix",
        subtitle_role: "You are the assignee for this finding. Update the status, add notes, and upload photos when resolved.",
        store: "Store",
        category: "Category",
        severity: "Severity",
        description: "Description",
        notes: "Notes / observations",
        notes_placeholder: "Describe what you did, when, and any observation…",
        upload_photo: "Upload evidence photo",
        add_more_photo: "Add another photo",
        save_notes: "Save comment",
        photos: "Evidence photos",
        no_photos: "No photos yet.",
        author_label: "Your name (optional)",
        author_placeholder: "To record who updated",
        save: "Save changes",
        saved: "Changes saved",
        save_error: "Could not save",
        upload_error: "Could not upload the photo",
        last_updated: "Last updated: {{when}}",
        last_updated_by: "Updated by: {{by}}",
        translate_desc_en: "Translate description to EN",
        translate_desc_es: "Translate description to ES",
        translate_note_en: "Translate note to EN",
        translate_note_es: "Translate note to ES",
        translated: "Translated",
        translate_error: "Translation failed",
        show_original: "Show original",
      },
      public: {
        title: "Public report",
        readonly: "Read only",
        unavailable: "Link unavailable",
        expired: "Link expired",
        expired_hint: "Ask the auditor for a new updated link.",
        go_home: "Go home",
        signatures: "Signatures",
        signed_at: "Signed: {{when}}",
      },
      share_app_msg: "Access the Mueblería Canales Operations Audit app",
      share_app_copied: "App link copied",
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["es", "en"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "canales.lang",
    },
  });

export default i18n;
